import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Text, Stack, Inline } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { messages } from '../../messages.ts';

type LoadingType = 'bulk' | 'single';

type LoadingProps = {
	loadingType?: LoadingType;
};

const GeneratingAnimation = (props: { alt: string }) => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<title>{props.alt}</title>
		<circle cx="5.5" cy="12.5" r="1.5" fill="#0C66E4">
			<animate
				attributeName="cy"
				values="11;13;13;13;13;13;13;11"
				dur="1s"
				repeatCount="indefinite"
				begin="0s"
			/>
		</circle>
		<circle cx="11.5" cy="10.5" r="1.5" fill="#1D7AFC">
			<animate
				attributeName="cy"
				values="11;13;13;13;13;13;13;11"
				dur="1s"
				repeatCount="indefinite"
				begin="0.2s"
			/>
		</circle>
		<circle cx="17.5" cy="12.5" r="1.5" fill="#388BFF">
			<animate
				attributeName="cy"
				values="11;13;13;13;13;13;13;11"
				dur="1s"
				repeatCount="indefinite"
				begin="0.4s"
			/>
		</circle>
	</svg>
);

const loadingContainerStyles = xcss({ height: '100%', padding: 'space.300' });
const loadingIconStyles = xcss({ width: '24px', height: '24px' });

export const IssueCreateAiLoading = ({ loadingType = 'single' }: LoadingProps) => {
	const loadingMessage = useMemo(() => {
		if (fg('confluence-issue-terminology-refresh')) {
			const loadingSingleMessageRefresh = expVal<boolean>(
				'aiwc_confluence_update_loading_message',
				'isEnabled',
				false,
			)
				? expVal('confluence_work_item_create_ai_rebrand_to_rovo', 'isEnabled', false)
					? messages.sidePanelAiLoadingIssueExperimentRebrandToRovo
					: messages.sidePanelAiLoadingIssueExperiment
				: messages.sidePanelAiLoadingIssueTermRefresh;
			return loadingType === 'bulk'
				? messages.sidePanelAiLoadingBulkIssueTermRefresh
				: loadingSingleMessageRefresh;
		}

		const loadingSingleMessage = expVal<boolean>(
			'aiwc_confluence_update_loading_message',
			'isEnabled',
			false,
		)
			? expVal('confluence_work_item_create_ai_rebrand_to_rovo', 'isEnabled', false)
				? messages.sidePanelAiLoadingIssueExperimentRebrandToRovo
				: messages.sidePanelAiLoadingIssueExperiment
			: messages.sidePanelAiLoading;

		return loadingType === 'bulk' ? messages.sidePanelAiLoadingBulk : loadingSingleMessage;
	}, [loadingType]);

	return (
		<Stack xcss={loadingContainerStyles} alignBlock="stretch">
			<Inline space="space.050" alignBlock="center">
				<Box xcss={loadingIconStyles}>
					{/* TODO: Update the attribute below so it is translated. */}
					{/* eslint-disable-next-line no-restricted-syntax */}
					<GeneratingAnimation alt="Generating" />
				</Box>
				<Text size="large" color="color.text.subtlest">
					<FormattedMessage {...loadingMessage} />
				</Text>
			</Inline>
		</Stack>
	);
};
